import { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { ArrowLeft, Clock, MapPin, RefreshCw } from 'lucide-react';
import CorrectiveActionWorkflow from '@/components/CorrectiveActionWorkflow';
import useFeatureFlags from '@/hooks/useFeatureFlags';

const STATUS_LABEL = {
  open: 'statusOpen',
  assigned: 'statusAssigned',
  in_progress: 'statusInProgress',
  pending_verification: 'statusPendingVerification',
  closed: 'statusClosed',
  dismissed: 'statusDismissed',
};

/** Corrective actions assigned to the signed-in user: the assignee's own view of the workflow. */
const MyCorrectiveActions = ({ user }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const flags = useFeatureFlags();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/company/corrective-actions?days=365&assigned_to_me=true');
      setActions((data.correctiveActions || []).filter((a) => !a.isFromAudit));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load corrective actions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (flags.phase3CorrectiveActions === false) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="my-corrective-actions-page">
      <div className="max-w-4xl mx-auto">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />{t('correctiveWorkflow.back')}
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{t('correctiveWorkflow.myActions')}</CardTitle>
                <CardDescription>{t('correctiveWorkflow.myActionsDescription')}</CardDescription>
              </div>
              <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-actions-btn">
                <RefreshCw className="w-4 h-4 mr-2" />{t('correctiveWorkflow.refresh')}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {actions.length === 0 && !loading && (
              <p className="text-gray-500 text-center py-10" data-testid="no-assigned-actions">
                {t('correctiveWorkflow.noneAssigned')}
              </p>
            )}
            {actions.map((action) => (
              <div
                key={action.id}
                className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => setSelected(action)}
                data-testid={`assigned-action-${action.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="font-medium">{action.title}</p>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-500">
                      {action.location && (
                        <span><MapPin className="w-3 h-3 inline mr-1" />{action.location}</span>
                      )}
                      {action.dueDate && (
                        <span><Clock className="w-3 h-3 inline mr-1" />
                          {t('correctiveWorkflow.dueDate')}: {new Date(action.dueDate).toLocaleDateString(i18n.language)}
                        </span>
                      )}
                      {action.createdByName && (
                        <span>{t('correctiveWorkflow.assignedBy')}: {action.createdByName}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge variant="outline" data-testid={`assigned-action-status-${action.id}`}>
                      {t(`correctiveWorkflow.${STATUS_LABEL[action.status] || 'statusOpen'}`)}
                    </Badge>
                    {action.isOverdue && (
                      <Badge className="bg-red-100 text-red-800">{t('correctiveWorkflow.overdue')}</Badge>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('correctiveWorkflow.title')}</DialogTitle>
            <DialogDescription>{selected?.title}</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              <p className="text-sm text-gray-700 whitespace-pre-line">{selected.description}</p>
              <CorrectiveActionWorkflow
                action={selected}
                currentUserId={user?.id}
                canVerify={['company_admin', 'division_admin', 'super_admin', 'supervisor'].includes(user?.role)}
                onUpdated={async (updated) => {
                  let fresh = updated;
                  if (!fresh) {
                    const { data } = await axiosInstance.get(`/company/corrective-actions/${selected.id}`);
                    fresh = data;
                  }
                  setSelected(fresh);
                  load();
                }}
              />
              {selected.history?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-1">
                    {t('correctiveWorkflow.history')}
                  </p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {[...selected.history].reverse().map((entry, idx) => (
                      <div key={idx} className="text-xs p-2 bg-gray-50 rounded">
                        <span className="font-medium">{entry.by}</span>{' '}
                        {t(`correctiveWorkflow.historyAction.${entry.action}`, { defaultValue: entry.action })}
                        {entry.details ? ` — ${entry.details}` : ''}
                        <span className="text-gray-400 ml-2">
                          {new Date(entry.timestamp).toLocaleDateString(i18n.language)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyCorrectiveActions;
